/**
 * @file NavMegaMenu.tsx
 * @brief Top-bar mega menu — rail-aligned flush GridBlock navigation.
 *
 * @details
 * Panel sits under the topbar on the same ~1280px rail as page grids.
 * Quick access and Browse are hairline cells — no card
 * chrome. Related: GridBlock.tsx.
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — flush GridBlock mega menu
 */

import { ChevronRight, Search, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/Input";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { cn } from "@/shared/theme/cn";
import type { NavLinkItem, NavShellConfig } from "./nav-types";
import {
  flattenGroups,
  filterNavGroups,
  isHighlightActive,
  isNavBranchActive,
  isNavItemActive,
  navItemKey,
} from "./nav-utils";
import { shellOverlayMaxClass } from "./shell-rail";

const CLOSE_MS = 280;
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

const flatFilterControlClass =
  "h-11 w-full rounded-none border-0 bg-transparent px-4 shadow-none ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0";

interface NavMegaMenuProps {
  config: NavShellConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function staggerStyle(visible: boolean, delayMs: number): CSSProperties {
  return {
    transitionProperty: "opacity, transform",
    transitionDuration: "380ms",
    transitionTimingFunction: EASE,
    transitionDelay: visible ? `${delayMs}ms` : "0ms",
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(10px)",
  };
}

export function NavMegaMenu({
  config,
  open,
  onOpenChange,
}: NavMegaMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [expandedBranches, setExpandedBranches] = useState<Set<string>>(
    () => new Set(),
  );
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }

    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(frame);
    }

    setVisible(false);
    closeTimer.current = setTimeout(() => {
      setMounted(false);
      closeTimer.current = null;
    }, CLOSE_MS);

    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setFilter("");
  }, [open]);

  useEffect(() => {
    if (!open) setExpandedBranches(new Set());
  }, [open]);

  const toggleBranch = useCallback((key: string) => {
    setExpandedBranches((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const isFiltering = filter.trim().length > 0;

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mounted, onOpenChange]);

  /* Browse leaves out what Quick access already shows (DES 02-10-2026,
     "quick actions main links hain wo right per show karna bewakoofi hai,
     repeatation hai"). A row with sub-pages stays (Reports), since those
     pages open only from here; a group left empty goes. Filtering still
     searches every page, Quick access included. */
  const browseGroups = useMemo(() => {
    const quick = new Set(config.highlights.map((h) => h.to));
    return config.groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) => (item.children?.length ?? 0) > 0 || !quick.has(item.to),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [config.groups, config.highlights]);

  const filteredGroups = useMemo(
    () => filterNavGroups(filter.trim() ? config.groups : browseGroups, filter),
    [config.groups, browseGroups, filter],
  );

  /* Top-level Browse rows nest by path (`/company/meetings/new` under
     `/company/meetings`); only the longest match stays active. */
  const browsePeers = useMemo(
    () => config.groups.flatMap((g) => g.items),
    [config.groups],
  );

  // Every page, Quick access included, each once: Browse no longer
  // repeats Quick access, so its own count would undercount.
  const totalBrowseCount = useMemo(() => {
    const paths = new Set(config.highlights.map((h) => h.to));
    for (const item of flattenGroups(config.groups)) paths.add(item.to);
    return paths.size;
  }, [config.groups, config.highlights]);

  const close = () => onOpenChange(false);

  const go = (to: string) => {
    close();
    navigate(to);
  };

  if (!mounted) return null;

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close navigation menu"
        tabIndex={visible ? 0 : -1}
        className={cn(
          "fixed inset-0 z-40 bg-black/45 backdrop-blur-[3px] transition-opacity duration-300 ease-out",
          visible ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${config.scopeTitle} navigation`}
        className={cn(
          // From md the panel stops at the viewport and only the Browse list
          // scrolls (DES 04-10-2026: an open "Reports" scrolled the whole
          // menu). Below md the columns stack, so the panel scrolls as one.
          "fixed inset-x-0 top-14 z-50 flex max-h-[calc(100vh-3.5rem)] flex-col overflow-y-auto md:overflow-hidden",
          !visible && "pointer-events-none",
        )}
        style={{
          transitionProperty: "opacity, transform",
          transitionDuration: visible ? "420ms" : "240ms",
          transitionTimingFunction: EASE,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(-8px)",
        }}
      >
        <div className={cn(shellOverlayMaxClass, "md:flex md:min-h-0 md:w-full md:flex-col")}>
          {/* Keeps its own white: menus sit over the page (DES 30-09-2026). */}
          <GridBlock className="bg-background md:flex md:min-h-0 md:flex-col">
            <GridBand
              className="flex shrink-0 items-center justify-between gap-3 py-3"
              style={staggerStyle(visible, 40)}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">{config.scopeTitle}</p>
                {config.scopeSubtitle ? (
                  <p className="truncate text-xs text-muted-foreground">
                    {config.scopeSubtitle}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={close}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-none text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </button>
            </GridBand>

            <div className="grid md:min-h-0 md:flex-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] md:grid-rows-[minmax(0,1fr)]">
              {/* Scrolls only on a window too short for Quick access itself. */}
              <div className="min-h-0 border-b border-r border-border/60 md:overflow-y-auto">
                <div
                  className="border-b border-border/60 bg-muted/25 px-4 py-2.5 md:px-5"
                  style={staggerStyle(visible, 70)}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Quick access
                  </p>
                </div>

                <GridCells className="grid-cols-3 [&>*:nth-child(3n)]:border-r-0">
                  {config.highlights.map((item, index) => {
                    const Icon = item.icon;
                    const active = isHighlightActive(
                      location.pathname,
                      location.hash,
                      item,
                      config.highlights,
                    );
                    return (
                      <GridCell
                        key={item.to}
                        padded={false}
                        style={staggerStyle(visible, 100 + index * 40)}
                      >
                        <button
                          type="button"
                          onClick={() => go(item.to)}
                          title={item.description ?? item.label}
                          className={cn(
                            "flex h-full min-h-[4.5rem] w-full flex-col items-start gap-1.5 rounded-none border-0 bg-transparent px-3 py-3 text-left transition-colors sm:px-4",
                            active
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-muted/40",
                          )}
                        >
                          <Icon
                            className={cn(
                              "h-5 w-5 shrink-0",
                              active ? "text-primary" : "text-foreground",
                            )}
                          />
                          <span className="min-w-0 w-full">
                            <span className="block truncate text-sm font-semibold tracking-tight">
                              {item.label}
                            </span>
                            {item.description ? (
                              <span
                                className={cn(
                                  "mt-0.5 block line-clamp-2 text-[11px] leading-snug",
                                  active
                                    ? "text-primary/70"
                                    : "text-muted-foreground",
                                )}
                              >
                                {item.description}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </GridCell>
                    );
                  })}
                </GridCells>

                {/* No "At a glance" sessions summary (DES 02-10-2026: "we
                    should also remove at a glance from the menu"): the menu
                    navigates; the numbers live on Home and Analytics. */}
              </div>

              {/* Height follows the content (DES 02-10-2026, "mega menu ki
                  hight dynamic karo fix nahin"): the row is as tall as the
                  taller column, up to the viewport; past it only the list
                  below scrolls, inside this column (04-10-2026). */}
              <div className="flex min-h-0 flex-col border-b border-r border-border/60">
                <div
                  className="shrink-0 border-b border-border/60 bg-muted/25 px-4 py-2.5 md:px-5"
                  style={staggerStyle(visible, 120)}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Browse
                  </p>
                </div>

                <div
                  className="relative shrink-0 border-b border-border/60"
                  style={staggerStyle(visible, 150)}
                >
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground md:left-5" />
                  <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter pages…"
                    className={cn(flatFilterControlClass, "pl-10 md:pl-11")}
                    autoFocus={visible}
                  />
                </div>

                <div
                  className="min-h-0 flex-1 overflow-y-auto"
                  style={staggerStyle(visible, 180)}
                >
                  {filteredGroups.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-muted-foreground md:px-5">
                      {isFiltering ? (
                        <>No pages match &ldquo;{filter}&rdquo;</>
                      ) : (
                        "Every page is in Quick access."
                      )}
                    </p>
                  ) : (
                    filteredGroups.map((group) => (
                      <div key={group.id}>
                        <div className="border-b border-border/60 bg-muted/15 px-4 py-2 md:px-5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {group.label}
                          </p>
                        </div>
                        <ul className="grid grid-cols-1 md:grid-cols-2 [&>li]:border-b [&>li]:border-border/60 [&>li:last-child]:border-b-0 md:[&>li:nth-child(odd):not(:last-child)]:border-r md:[&>li:nth-last-child(2):nth-child(odd)]:border-b-0">
                          {group.items.map((item) => (
                            <NavBrowseItem
                              key={navItemKey(item)}
                              item={item}
                              pathname={location.pathname}
                              hash={location.hash}
                              onNavigate={close}
                              expandedBranches={expandedBranches}
                              onToggleBranch={toggleBranch}
                              forceExpanded={isFiltering}
                              peers={browsePeers}
                            />
                          ))}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <GridBand
              className="flex shrink-0 items-center justify-between gap-3 bg-muted/25 py-3 text-xs"
              style={staggerStyle(visible, 220)}
            >
              <span className="text-muted-foreground">
                {totalBrowseCount} {totalBrowseCount === 1 ? "page" : "pages"} in
                this workspace
              </span>
              <button
                type="button"
                onClick={() => go(config.homeTo)}
                className="font-medium text-brand hover:underline"
              >
                Back to dashboard
              </button>
            </GridBand>
          </GridBlock>
        </div>
      </div>
    </>,
    document.body,
  );
}

interface NavBrowseItemProps {
  item: NavLinkItem;
  pathname: string;
  hash: string;
  onNavigate: () => void;
  expandedBranches: Set<string>;
  onToggleBranch: (key: string) => void;
  forceExpanded?: boolean;
  nested?: boolean;
  parent?: NavLinkItem;
  /** Top-level rows across all groups, for longest-match active state. */
  peers?: NavLinkItem[];
}

function navBrowseLinkClass(active: boolean, nested?: boolean) {
  return cn(
    "flex min-w-0 flex-1 items-center gap-3 rounded-none px-4 text-sm transition-colors duration-150 md:px-5",
    nested ? "py-2.5 pl-8 md:pl-10" : "py-3",
    active
      ? "bg-primary/10 font-medium text-primary"
      : "text-foreground hover:bg-muted/40",
  );
}

function NavBrowseItem({
  item,
  pathname,
  hash,
  onNavigate,
  expandedBranches,
  onToggleBranch,
  forceExpanded = false,
  nested = false,
  parent,
  peers,
}: NavBrowseItemProps) {
  const Icon = item.icon;
  const hasChildren = (item.children?.length ?? 0) > 0;
  const branchKey = navItemKey(item);
  const sectionActive = isNavBranchActive(pathname, hash, item);
  const linkActive =
    isNavItemActive(pathname, hash, item, parent) &&
    (!peers || isHighlightActive(pathname, hash, item, peers));
  const isExpanded =
    hasChildren && (forceExpanded || expandedBranches.has(branchKey));

  if (!hasChildren) {
    return (
      <li>
        <NavLink
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={navBrowseLinkClass(linkActive, nested)}
        >
          <Icon
            className={cn(
              "shrink-0 opacity-70",
              nested ? "h-3.5 w-3.5" : "h-4 w-4",
            )}
          />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {!nested ? (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-40" />
          ) : null}
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <div
        className={cn(
          "flex items-stretch",
          linkActive && !isExpanded && "bg-primary/5",
        )}
      >
        <NavLink
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={navBrowseLinkClass(linkActive)}
        >
          <Icon className="h-4 w-4 shrink-0 opacity-70" />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </NavLink>
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? `Collapse ${item.label}` : `Expand ${item.label}`
          }
          onClick={() => onToggleBranch(branchKey)}
          className={cn(
            "flex w-11 shrink-0 items-center justify-center rounded-none border-l border-border/60 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground",
            sectionActive && "text-primary",
          )}
        >
          <ChevronRight
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              isExpanded && "rotate-90",
            )}
          />
        </button>
      </div>
      <div
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          isExpanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0",
        )}
      >
        <ul className="overflow-hidden border-l border-border/60 divide-y divide-border/60 ml-4 md:ml-5">
          {item.children?.map((child) => (
            <NavBrowseItem
              key={navItemKey(child)}
              item={child}
              pathname={pathname}
              hash={hash}
              onNavigate={onNavigate}
              expandedBranches={expandedBranches}
              onToggleBranch={onToggleBranch}
              forceExpanded={forceExpanded}
              parent={item}
              nested
            />
          ))}
        </ul>
      </div>
    </li>
  );
}
